import React, { useState, useEffect } from 'react';
import { Player, RoundScore } from '../types';
import { calculateRoundScore, getMaxBidForRound } from '../utils/gameLogic';

interface RoundInputProps {
  players: Player[];
  currentRound: number;
  onCompleteRound: (roundScores: RoundScore[]) => void;
  dealerIndex: number | null;
  onUpdateCurrentRoundData: (bids: { [playerId: string]: number }, tricks: { [playerId: string]: number }) => void;
}

const RoundInput: React.FC<RoundInputProps> = ({ players, currentRound, onCompleteRound, dealerIndex, onUpdateCurrentRoundData }) => {
  const [bids, setBids] = useState<{ [playerId: string]: number }>({});
  const [tricks, setTricks] = useState<{ [playerId: string]: number }>({});
  const [isBiddingPhase, setIsBiddingPhase] = useState(true);

  const maxBid = getMaxBidForRound(currentRound, players.length);

  // Initialize all players with 0 bids when component mounts or players change
  useEffect(() => {
    const initialBids: { [playerId: string]: number } = {};
    const initialTricks: { [playerId: string]: number } = {};
    players.forEach(player => {
      initialBids[player.id] = 0;
      initialTricks[player.id] = 0;
    });
    setBids(initialBids);
    setTricks(initialTricks);
    setIsBiddingPhase(true);
  }, [players, currentRound]);

  const handleBidSelect = (playerId: string, bidValue: number) => {
    setBids(prev => ({
      ...prev,
      [playerId]: bidValue
    }));
  };

  const handleTrickSelect = (playerId: string, trickValue: number) => {
    setTricks(prev => ({
      ...prev,
      [playerId]: trickValue
    }));
  };

  const getTotalBids = () => {
    return Object.values(bids).reduce((sum: number, bid) => sum + bid, 0);
  };

  const getTotalTricks = () => {
    return Object.values(tricks).reduce((sum: number, trick) => sum + trick, 0);
  };

  const isBiddingValid = () => {
    const totalBids = getTotalBids();
    return totalBids !== maxBid;
  };

  const isTricksValid = () => {
    const totalTricks = getTotalTricks();
    return totalTricks === maxBid;
  };

  const getBiddingValidationMessage = () => {
    const totalBids = getTotalBids();
    if (totalBids === maxBid) {
      return `Invalid: Total bids (${totalBids}) cannot equal max tricks (${maxBid})`;
    }
    return `Total bids: ${totalBids} / Max tricks: ${maxBid}`;
  };

  const getTricksValidationMessage = () => {
    const totalTricks = getTotalTricks();
    if (totalTricks === maxBid) {
      return `Valid: Total tricks (${totalTricks}) equals cards dealt (${maxBid})`;
    } else if (totalTricks > maxBid) {
      return `Invalid: Total tricks (${totalTricks}) cannot exceed cards dealt (${maxBid})`;
    } else {
      return `Invalid: Total tricks (${totalTricks}) must equal cards dealt (${maxBid})`;
    }
  };

  const handleStartTricks = () => {
    const allBidsEntered = players.every(player => player.id in bids);
    if (allBidsEntered && isBiddingValid()) {
      setIsBiddingPhase(false);
    }
  };

  const handleCompleteRound = () => {
    const allTricksEntered = players.every(player => player.id in tricks);
    if (allTricksEntered && isTricksValid()) {
      const roundScores: RoundScore[] = players.map(player => ({
        playerId: player.id,
        roundNumber: currentRound,
        bid: bids[player.id] ?? 0,
        tricks: tricks[player.id] ?? 0,
        score: calculateRoundScore(bids[player.id] ?? 0, tricks[player.id] ?? 0)
      }));
      
      onCompleteRound(roundScores);
      
      // Reset state
      setBids({});
      setTricks({});
      setIsBiddingPhase(true);
    }
  };

  useEffect(() => {
    onUpdateCurrentRoundData(bids, tricks);
  }, [bids, tricks, onUpdateCurrentRoundData]);

  return (
    <div className="bg-white rounded-none shadow-lg p-8 mb-7 border-none">
      <h2 className="text-2xl font-bold mb-4">
        Round {currentRound} - {isBiddingPhase ? 'Bidding Phase' : 'Tricks Phase'}
      </h2>
      
      <div className="mb-4 p-4 bg-yellow-100 text-yellow-800 rounded-md">
        <p className="text-center font-bold">
          Max bid for this round: {maxBid} tricks
        </p>
      </div>

      {isBiddingPhase && (
        <div className={`mb-4 p-4 rounded-md ${isBiddingValid() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          <p className="text-center font-bold">
            {getBiddingValidationMessage()}
          </p>
        </div>
      )}

      {!isBiddingPhase && (
        <div className={`mb-4 p-4 rounded-md ${isTricksValid() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          <p className="text-center font-bold">
            {getTricksValidationMessage()}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {players.map((player, idx) => (
          <div key={player.id} className={`p-4 border rounded-md ${dealerIndex === idx ? 'bg-blue-100 border-blue-400' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className="font-bold mb-2 flex items-center gap-2">
              {player.name}
              {dealerIndex === idx && (
                <span className="bg-blue-500 text-white px-2 py-1 rounded-md">
                  DEALER
                </span>
              )}
            </h3>
            
            {isBiddingPhase ? (
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <span className="block text-sm font-medium mb-1">Bid:</span>
                  <div className="flex gap-2">
                    {Array.from({ length: maxBid + 1 }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => handleBidSelect(player.id, i)}
                        className={`px-3 py-2 text-sm font-bold rounded-md transition-all ${
                          (bids[player.id] || 0) === i 
                            ? 'bg-purple-600 text-white shadow-md' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <span className="block text-sm font-medium mb-1">Bid:</span>
                  <div className="p-2 bg-gray-100 rounded-md">
                    {bids[player.id] || 0}
                  </div>
                </div>
                <label className="flex-1">
                  <span className="block text-sm font-medium mb-1">Tricks:</span>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: maxBid + 1 }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => handleTrickSelect(player.id, i)}
                        className={`px-3 py-2 text-sm font-bold rounded-md transition-all ${
                          (tricks[player.id] || 0) === i 
                            ? 'bg-purple-600 text-white shadow-md' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                </label>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-center gap-4">
        {isBiddingPhase ? (
          <button
            onClick={handleStartTricks}
            disabled={!players.every(player => player.id in bids) || !isBiddingValid()}
            className="btn disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Tricks Phase
          </button>
        ) : (
          <>
            <button
              onClick={() => setIsBiddingPhase(true)}
              className="btn-secondary"
            >
              Edit Bids
            </button>
            <button
              onClick={handleCompleteRound}
              disabled={!players.every(player => player.id in tricks) || !isTricksValid()}
              className="btn disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Complete Round
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RoundInput; 